// @ts-expect-error - ringover-sdk has no types
import RingoverSDK from "ringover-sdk";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useUser } from "@/providers/user";
import { DataService } from "@/services/data";
import { walterAssistantClient, type AssistantCallEventPayload } from "@/services/walter-assistant";

type RingoverContextType = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isReady: boolean;
    dialNumber: (phoneNumber: string, recordId?: string | null, recordType?: AssistantCallEventPayload["record_type"]) => void;
};

type RingoverCallEvent = {
    action: string;
    data: {
        direction: "in" | "out";
        from_number: string;
        to_number: string;
        internal: boolean;
        call_id: string;
        ringDuration: number;
        callDuration: number;
    };
};

const RingoverContext = React.createContext<RingoverContextType>(null!);

function formatPhoneToE164(number: string | null): string {
    if (!number) return "";
    let num = String(number).replace(/\s/g, "").replace(/\+/g, "");
    if (num.startsWith("0")) {
        num = "33" + num.slice(-9);
    }
    return num ? `+${num}` : "";
}

export function RingoverProvider({ children }: { children: React.ReactNode }) {
    const { user } = useUser();
    const [open, setOpen] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const pendingRecordIdRef = useRef<string | null>(null);
    const pendingRecordTypeRef = useRef<AssistantCallEventPayload["record_type"]>(null);
    const sdkRef = useRef<{
        dial: (n: string, f?: string) => boolean;
        show: () => boolean;
        generate: () => unknown;
        checkStatus: (allowNotReady?: boolean | number) => boolean;
        off: () => boolean;
        destroy: () => boolean;
    } | null>(null);
    const initializedRef = useRef(false);
    const userRef = useRef(user);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const postCoachingCallEvent = useCallback((eventType: "call.answered" | "call.ended", event: RingoverCallEvent) => {
        const currentUser = userRef.current;
        if (!currentUser?.walter_assistant_coach || !currentUser.email || !event.data.call_id || event.data.internal) return;

        const recordId = eventType === "call.answered" ? (pendingRecordIdRef.current || null) : null;
        const recordType = eventType === "call.answered" ? (pendingRecordTypeRef.current || null) : null;
        if (eventType === "call.ended") {
            pendingRecordIdRef.current = null;
            pendingRecordTypeRef.current = null;
        }

        walterAssistantClient
            .postCallEvent({
                event_type: eventType,
                voip_provider: "ringover",
                voip_call_id: event.data.call_id,
                agent_email: currentUser.email,
                agent_first_name: currentUser.sf_user?.first_name || null,
                agent_last_name: currentUser.sf_user?.last_name || null,
                agent_process: currentUser.sf_user?.sales_process || null,
                record_id: recordId,
                record_type: recordType,
                prospect_phone: event.data.direction === "out" ? event.data.to_number : event.data.from_number,
                occurred_at: new Date().toISOString(),
                metadata: {
                    ...event.data,
                    source_event: event.action
                }
            })
            .catch(error => {
                console.error("Ringover coaching call event error:", error);
            });
    }, []);

    useEffect(() => {
        let cancelled = false;

        let retryCount = 0;
        const maxRetries = 40; // ~12 seconds

        const tryInit = () => {
            if (cancelled || retryCount >= maxRetries) return;
            const container = document.getElementById("ringover-cti-container");
            if (!container) {
                retryCount++;
                setTimeout(tryInit, 300);
                return;
            }
            if (initializedRef.current) return;
            initializedRef.current = true;

            const sdk = new RingoverSDK({
                container: "ringover-cti-container",
                trayicon: false,
                animation: false,
                size: "medium",
                type: "relative",
                border: false
            });

            sdkRef.current = sdk;

            sdk.on("dialerReady", () => {
                if (!cancelled) setIsReady(true);
            });

            sdk.on("login", () => {
                if (!cancelled) setIsReady(true);
            });

            sdk.on("logout", () => {
                if (!cancelled) setIsReady(false);
            });

            sdk.on(
                "ringingCall",
                (e: RingoverCallEvent) => {
                    try {
                        DataService.client
                            .url("/voice/call_agents/log_call/")
                            .post({
                                provider: "ringover",
                                call_id: e.data.call_id,
                                direction: e.data.direction == "out" ? "outbound" : "inbound",
                                from_number: e.data.from_number,
                                to_number: e.data.to_number
                            })
                            .json()
                            .then(data => {
                                console.log("Call logged successfully", data);
                            })
                            .catch(e => {
                                console.error("Call log error:", e);
                            });
                    } catch (e) {
                        console.error("ringingCall error:", e);
                    }
                }
            );

            sdk.on("answeredCall", (e: RingoverCallEvent) => {
                postCoachingCallEvent("call.answered", e);
            });

            sdk.on("hangupCall", (e: RingoverCallEvent) => {
                postCoachingCallEvent("call.ended", e);
            });

            try {
                sdk.generate();
                // Fix: add HID permission for Ringover CTI (required for call initiation)
                const iframe = container.querySelector("iframe");
                if (iframe) {
                    const currentAllow = iframe.getAttribute("allow") || "";
                    if (!currentAllow.includes("hid")) {
                        iframe.setAttribute("allow", `${currentAllow}${currentAllow ? ";" : ""} hid 'self'`);
                    }
                }
            } catch {
                // SDK may not be ready yet
            }
        };

        tryInit();

        return () => {
            cancelled = true;
            const sdk = sdkRef.current;
            if (sdk) {
                try {
                    sdk.off();
                    if (sdk.checkStatus()) {
                        sdk.destroy();
                    }
                } catch {
                    // ignore
                }
                sdkRef.current = null;
            }
            initializedRef.current = false;
        };
    }, [postCoachingCallEvent]);

    // Show iframe when panel opens
    useEffect(() => {
        if (open && sdkRef.current) {
            try {
                sdkRef.current.show();
            } catch {
                // ignore
            }
        }
    }, [open]);

    const dialNumber = (phoneNumber: string, recordId?: string | null, recordType?: AssistantCallEventPayload["record_type"], retry = true) => {
        if (recordId && recordType) {
            pendingRecordIdRef.current = recordId;
            pendingRecordTypeRef.current = recordType;
        }
        setOpen(true);
        const sdk = sdkRef.current;
        const formatted = formatPhoneToE164(phoneNumber);

        if (!formatted) {
            toast.error("Numéro de téléphone invalide");
            return;
        }

        try {
            if (sdk && sdk.checkStatus(true)) {
                if (!sdk.checkStatus(2)) {
                    sdk.generate();
                    // Apply HID permission if generate was just called
                    const container = document.getElementById("ringover-cti-container");
                    const iframe = container?.querySelector("iframe");
                    if (iframe && !iframe.getAttribute("allow")?.includes("hid")) {
                        const currentAllow = iframe.getAttribute("allow") || "";
                        iframe.setAttribute("allow", `${currentAllow}${currentAllow ? ";" : ""} hid 'self'`);
                    }
                }
                // Show iframe first so it can receive and process the dial message
                sdk.show();
                // Small delay to ensure iframe is ready to handle the postMessage
                setTimeout(() => {
                    sdk.dial(formatted);
                }, 300);
            } else {
                if (retry) {
                    setTimeout(() => dialNumber(phoneNumber, recordId, recordType, false), 1000);
                } else {
                    toast.error("Ringover n'est pas disponible. Veuillez ouvrir l'onglet Ringover et vous connecter.");
                }
            }
        } catch (e) {
            console.error("Ringover dial error:", e);
            toast.error("Une erreur est survenue, veuillez réessayer");
        }
    };

    const value = { open, setOpen, isReady, dialNumber };
    return <RingoverContext.Provider value={value}>{children}</RingoverContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRingover() {
    return useContext(RingoverContext);
}
